import { inject } from '@angular/core';
import { Params } from '@angular/router';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
    NotificationMessageLevel,
    NotificationService,
    SortDirection,
    StorageKey,
    StorageService,
} from '@lib/core';
import { CompanyCodeDataService } from '@lib/company-code';
import { MaterialGroupDataService, MaterialGroupStatus } from '@lib/material-group';
import { catchError, finalize, forkJoin, map, Observable, of, throwError } from 'rxjs';
import { PurchasingGroup } from '../models/purchasing-group.model';
import { PurchasingGroupAssignableUser } from '../models/purchasing-group-assignable-user.model';
import { PurchasingGroupDataService } from '../services/purchasing-group-data.service';
import { PurchasingGroupFilterOption } from './types/purchasing-group-filter-option';
import {
    IPurchasingGroupListSortingDto,
    PurchasingGroupListSorting,
    PurchasingGroupSortBy,
} from './types/purchasing-group-list-sorting';

interface IPurchasingGroupListState {
    readonly purchasingGroups: ReadonlyArray<PurchasingGroup>;
    readonly loading: boolean;
    readonly sortBy: PurchasingGroupSortBy;
    readonly sortDirection: SortDirection;
    readonly companyCodeOptions: ReadonlyArray<PurchasingGroupFilterOption>;
    readonly materialGroupOptions: ReadonlyArray<PurchasingGroupFilterOption>;
    readonly memberOptions: ReadonlyArray<PurchasingGroupAssignableUser>;
}

const initialState: IPurchasingGroupListState = {
    purchasingGroups: [],
    loading: false,
    sortBy: PurchasingGroupSortBy.CODE,
    sortDirection: SortDirection.ASC,
    companyCodeOptions: [],
    materialGroupOptions: [],
    memberOptions: [],
};

export const PurchasingGroupListStore = signalStore(
    withState(initialState),
    withMethods(
        (
            store,
            dataService = inject(PurchasingGroupDataService),
            storageService = inject(StorageService),
            companyCodeDataService = inject(CompanyCodeDataService),
            materialGroupDataService = inject(MaterialGroupDataService),
            notificationService = inject(NotificationService),
        ) => ({
            async initSorting(params: Params): Promise<PurchasingGroupListSorting> {
                const sorting = await this.resolveInitialSorting(params);
                this.setSorting(sorting.sortBy, sorting.sortDirection);
                return sorting;
            },

            loadAll(): Observable<void> {
                patchState(store, { loading: true });
                return dataService.getAll().pipe(
                    map((purchasingGroups) => {
                        patchState(store, {
                            purchasingGroups: this.sortPurchasingGroups(purchasingGroups),
                        });
                        return undefined;
                    }),
                    catchError((error) => throwError(() => error)),
                    finalize(() => {
                        patchState(store, { loading: false });
                    }),
                );
            },

            loadFilterOptions(): Observable<void> {
                // Keep the dropdown usable even when one source fails — log the cause for dev
                // visibility and surface a non-blocking warning so the user understands why the
                // option list is empty/short.
                const reportFailure = (sourceLabel: string, fallbackMessage: string, error: unknown): void => {
                    console.error(`[purchasing-group-list] Failed to load ${sourceLabel}`, error);
                    notificationService.showNotification(fallbackMessage, NotificationMessageLevel.WARNING);
                };

                return forkJoin([
                    companyCodeDataService.getAll().pipe(
                        catchError((error) => {
                            reportFailure(
                                'company codes',
                                marker('Buchungskreise für den Filter konnten nicht geladen werden.'),
                                error,
                            );
                            return of([]);
                        }),
                    ),
                    materialGroupDataService.getAllMaterialGroups().pipe(
                        catchError((error) => {
                            reportFailure(
                                'material groups',
                                marker('Warengruppen für den Filter konnten nicht geladen werden.'),
                                error,
                            );
                            return of([]);
                        }),
                    ),
                    dataService.getAssignableUsers().pipe(
                        catchError((error) => {
                            reportFailure(
                                'assignable users',
                                marker('Mitarbeiter für den Filter konnten nicht geladen werden.'),
                                error,
                            );
                            return of([]);
                        }),
                    ),
                ]).pipe(
                    map(([companyCodes, materialGroups, members]) => {
                        const companyCodeOptions = [...companyCodes]
                            .map(
                                (cc) =>
                                    new PurchasingGroupFilterOption(
                                        cc.code ?? cc.id,
                                        cc.name ?? '',
                                        cc.active,
                                    ),
                            )
                            .sort((a, b) => {
                                if (a.active !== b.active) return a.active ? -1 : 1;
                                return a.code.localeCompare(b.code);
                            });
                        const materialGroupOptions = [...materialGroups]
                            .map(
                                (mg) =>
                                    new PurchasingGroupFilterOption(
                                        mg.code,
                                        mg.primaryName || mg.code,
                                        mg.status === MaterialGroupStatus.ACTIVE,
                                    ),
                            )
                            .sort((a, b) => {
                                if (a.active !== b.active) return a.active ? -1 : 1;
                                return a.code.localeCompare(b.code);
                            });
                        const memberOptions = [...members].sort((a, b) =>
                            a.valueDisplay.localeCompare(b.valueDisplay),
                        );
                        patchState(store, { companyCodeOptions, materialGroupOptions, memberOptions });
                        return undefined;
                    }),
                );
            },

            setSorting(sortBy: PurchasingGroupSortBy, sortDirection: SortDirection): void {
                patchState(store, {
                    sortBy,
                    sortDirection,
                    purchasingGroups: this.sortPurchasingGroups(
                        store.purchasingGroups(),
                        sortBy,
                        sortDirection,
                    ),
                });
                void storageService.setItem(
                    StorageKey.PURCHASING_GROUP_LIST_SORTING,
                    JSON.stringify(new PurchasingGroupListSorting(sortBy, sortDirection).toDto()),
                );
            },

            sortPurchasingGroups(
                list: ReadonlyArray<PurchasingGroup>,
                sortBy: PurchasingGroupSortBy = store.sortBy(),
                sortDirection: SortDirection = store.sortDirection(),
            ): ReadonlyArray<PurchasingGroup> {
                const directionFactor = sortDirection === SortDirection.DESC ? -1 : 1;
                return [...list].sort((a, b) => {
                    const valueA = sortBy === PurchasingGroupSortBy.NAME ? a.name : a.code;
                    const valueB = sortBy === PurchasingGroupSortBy.NAME ? b.name : b.code;
                    return valueA.localeCompare(valueB) * directionFactor;
                });
            },

            async resolveInitialSorting(params: Params): Promise<PurchasingGroupListSorting> {
                if (params['clean']) {
                    await storageService.removeItem(StorageKey.PURCHASING_GROUP_LIST_SORTING);
                    return PurchasingGroupListSorting.default();
                }

                if (PurchasingGroupListSorting.hasRouteParams(params)) {
                    return PurchasingGroupListSorting.fromRouteParams(params);
                }

                if (await storageService.has(StorageKey.PURCHASING_GROUP_LIST_SORTING)) {
                    const sorting = await storageService.getItemObject<IPurchasingGroupListSortingDto>(
                        StorageKey.PURCHASING_GROUP_LIST_SORTING,
                    );
                    return PurchasingGroupListSorting.fromDto(sorting);
                }

                return PurchasingGroupListSorting.default();
            },
        }),
    ),
);
